import axios, { AxiosError } from 'axios';

import { API_BASE_URL, API_ENDPOINTS, getAuthToken, setAuthToken } from '../config/constants';

// Auth API client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Mixed content sorununu çözmek için withCredentials ekledik
  withCredentials: false,
});

// Request interceptor - token eklemek için
apiClient.interceptors.request.use(
  (config) => {
    // Login isteği için token eklemeye gerek yok
    if (config.url === API_ENDPOINTS.AUTH.LOGIN) {
      return config;
    }
    
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error.message || 'Bilinmeyen hata');
    return Promise.reject(error);
  }
);

// console.log('Auth API base URL:', API_BASE_URL);

// Login request interface
interface LoginRequest {
  username: string;
  password: string;
  email?: string; // Opsiyonel, şifre sıfırlama vb. için
  rememberMe?: boolean; // Beni hatırla seçeneği
}

// Register request interface
interface RegisterRequest {
  email: string;
  password: string;
}

// Login response interface
interface LoginResponse {
  token: string;
  refreshToken: string;
  expiration: string;
  rememberMe: boolean;
}

// Session settings interface
interface SessionSettings {
  sessionTimeoutMinutes: number;
  rememberMe: boolean;
  autoRefreshSession: boolean;
  deviceInfo?: string;
  lastIpAddress?: string;
  lastLoginAt?: string;
  timeZone?: string;
  useSystemTimeZone?: boolean;
}

// Auth API methods
export const authApi = {
  // Login
  login: async (request: LoginRequest): Promise<LoginResponse> => {
    try {
      console.log('Login request:', { username: request.username, rememberMe: request.rememberMe });
      
      const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', {
        username: request.username,
        password: request.password,
        rememberMe: request.rememberMe
      });

      console.log('Login response received');

      // Token'ları sakla
      const { token, refreshToken } = response.data;
      
      if (request.rememberMe) {
        // Remember me seçiliyse localStorage'a kaydet
        localStorage.setItem('accessToken', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('token', token); // Backward compatibility
        console.log('Tokens saved to localStorage');
      } else {
        // Remember me seçili değilse sessionStorage'a kaydet
        sessionStorage.setItem('accessToken', token);
        sessionStorage.setItem('refreshToken', refreshToken);
        sessionStorage.setItem('token', token); // Backward compatibility
        console.log('Tokens saved to sessionStorage');
      }

      return response.data;
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Axios hatası mı kontrol et
      if (error.response) {
        const axiosError = error as AxiosError<any>;
        console.error('Login failed with status:', axiosError.response?.status);
        console.error('Login error data:', axiosError.response?.data);
        
        // Backend'den gelen hata mesajını kullan
        if (axiosError.response?.data?.message) {
          const backendError = new Error(axiosError.response.data.message);
          // @ts-ignore
          backendError.status = axiosError.response.status;
          throw backendError;
        }
        
        throw axiosError;
      }
      
      throw error;
    }
  },

  // Register
  register: async (request: RegisterRequest): Promise<void> => {
    try {
      console.log('Register request:', { email: request.email });
      
      await apiClient.post('/api/v1/auth/register', {
        email: request.email,
        password: request.password
      });

      console.log('Registration successful');
    } catch (error: any) {
      console.error('Register error:', error);
      
      // Axios hatası mı kontrol et
      if (error.response) {
        const axiosError = error as AxiosError<any>;
        console.error('Register failed with status:', axiosError.response?.status);
        console.error('Register error data:', axiosError.response?.data);
        
        // Backend'den gelen hata mesajını kullan
        if (axiosError.response?.data?.message) {
          const backendError = new Error(axiosError.response.data.message);
          // @ts-ignore
          backendError.status = axiosError.response.status;
          throw backendError;
        }
        
        throw axiosError;
      }
      
      throw error;
    }
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      console.log('Logging out and removing tokens');
      
      // Önce API'ye logout isteği gönder
      const token = getAuthToken();
      if (token) {
        await apiClient.post('/api/v1/auth/logout');
      }
      
      // Tüm token'ları temizle
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
    } catch (error) {
      console.error('Logout error:', error);
      // Hata olsa bile token'ları temizle
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
    }
  },
  
  // Get session settings
  getSessionSettings: async (): Promise<SessionSettings> => {
    // Hız sınırlama için son istek zamanını kontrol et
    const lastFetchTime = localStorage.getItem('lastSessionSettingsFetchTime');
    const now = Date.now();
    const throttleTime = 2000; // 2 saniye
    
    if (lastFetchTime && (now - parseInt(lastFetchTime)) < throttleTime) {
      // Sadece geliştirme modunda log göster
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_LOGS === 'true') {
        console.log('Session settings request throttled, using cached data');
      }
      
      const cachedSettings = localStorage.getItem('cachedSessionSettings');
      if (cachedSettings) {
        return JSON.parse(cachedSettings);
      }
      
      return {
        sessionTimeoutMinutes: 30,
        rememberMe: false,
        autoRefreshSession: false
      };
    }
    
    try {
      localStorage.setItem('lastSessionSettingsFetchTime', now.toString());
      
      const response = await apiClient.get('/api/v1/user-settings/session');
      
      localStorage.setItem('cachedSessionSettings', JSON.stringify(response.data));
      
      return response.data;
    } catch (error) {
      // Sadece geliştirme modunda log göster
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting session settings:', error);
      }
      return {
        sessionTimeoutMinutes: 30,
        rememberMe: false,
        autoRefreshSession: false
      };
    }
  },
  
  // Save session settings
  saveSessionSettings: async (settings: SessionSettings): Promise<SessionSettings> => {
    try {
      const response = await apiClient.post('/api/v1/user-settings/session', settings);
      return response.data;
    } catch (error) {
      console.error('Error saving session settings:', error);
      throw error;
    }
  },
  
  // Refresh session
  refreshSession: async (): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post<LoginResponse>('/api/v1/auth/refresh-session');
      return response.data;
    } catch (error) {
      console.error('Error refreshing session:', error);
      throw error;
    }
  },

  // Get current user data
  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/api/v1/auth/current-user');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      // Hata durumunda özel bir hata nesnesi fırlat
      const authError = new Error('Failed to get user data');
      // @ts-ignore
      authError.isAuthError = true; // AuthContext'te kontrol edilecek özel bir flag
      throw authError;
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    // İlk önce localStorage'dan token kontrolü yap
    const localToken = localStorage.getItem('accessToken') || localStorage.getItem('token');
    // Eğer localStorage'da yoksa sessionStorage'dan kontrol et
    const sessionToken = sessionStorage.getItem('accessToken') || sessionStorage.getItem('token');
    
    // Herhangi bir yerde token varsa kullanıcı giriş yapmış demektir
    const isTokenExists = Boolean(localToken || sessionToken);
    console.log('Authentication check:', isTokenExists ? 'Token exists' : 'No token found');
    return isTokenExists;
  },
  
  // Change password
  changePassword: async (request: { currentPassword: string; newPassword: string }): Promise<void> => {
    try {
      await apiClient.post('/api/v1/auth/change-password', request);
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },
  
  // Forgot password
  forgotPassword: async (request: { email: string; clientUrl: string }): Promise<void> => {
    try {
      await apiClient.post('/api/v1/auth/forgot-password', request);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  },
  
  // Reset password
  resetPassword: async (request: { token: string; email: string; newPassword: string }): Promise<void> => {
    try {
      await apiClient.post('/api/v1/auth/reset-password', request);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  },
  
  // Get user login logs
  getUserLoginLogs: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/api/v1/auth/login-logs');
      return response;
    } catch (error) {
      console.error('Error getting login logs:', error);
      throw error;
    }
  },

  // Get timezone settings
  getTimezoneSettings: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/api/v1/user-settings/timezone');
      return response.data;
    } catch (error) {
      console.error('Error getting timezone settings:', error);
      throw error;
    }
  },

  // Save timezone settings
  saveTimezoneSettings: async (settings: { timeZone?: string; useSystemTimeZone?: boolean }): Promise<any> => {
    try {
      const response = await apiClient.post('/api/v1/user-settings/timezone', settings);
      return response.data;
    } catch (error) {
      console.error('Error saving timezone settings:', error);
      throw error;
    }
  }
};